/**
 * CreateQuestion.jsx
 *
 * Dynamic form that changes its input fields based on the selected question type.
 *
 * Question Type → Form Schema:
 * ─────────────────────────────────────────────────────────────────────────────
 *  1 Single Choice    → N radio options  + correct_answer: { answer: optId }
 *  2 Multiple Choice  → N checkbox opts  + correct_answer: { answers: [ids] }
 *  3 True / False     → radio True/False + correct_answer: { answer: bool }
 *  4 Short Text       → no options       + correct_answer: null (AI graded)
 *  5 Essay            → no options       + correct_answer: null (AI graded)
 *  6 Fill in Blank    → blank count      + correct_answer: { answers: [str] }
 *  7 Matching         → left+right pairs + correct_answer: { pairs: [{left,right}] }
 *
 * Django JSONField structures are built in buildPayload() before submission.
 */

import { useState, useEffect } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Badge, FIELD_CLS } from '@/components/ui/primitives'
import RichTextEditor from '@/components/ui/RichTextEditor'
import { createQuestion, updateQuestion, listTopics, listQuestionBanks } from '@/api/resources'
import { Save, Plus, Trash2 } from 'lucide-react'
import { fetchAllPages } from '@/utils/fetchAll'

// ── Constants ────────────────────────────────────────────────────────────────
const QUESTION_TYPES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

const TYPE_COLORS = {
  1: 'blue', 2: 'purple', 3: 'amber',
  4: 'green', 5: 'green', 6: 'red', 7: 'blue',
  8: 'orange', 9: 'red', 10: 'pink', 11: 'teal', 12: 'yellow',
}

// ── Default form values ──────────────────────────────────────────────────────
function defaultValues(initial = {}) {
  return {
    question_bank:  initial.question_bank || '',
    question_type: initial.question_type || 1,
    content: initial.content || '',
    topic: initial.topic || '',
    score: initial.score || 1,
    // Type 1 & 2: options array
    options: initial.options?.length ? initial.options : [
      { id: 1, text: '' },
      { id: 2, text: '' },
    ],
    // Type 1: single correct option id
    correct_single: initial.correct_answer?.answer ?? 1,
    // Type 2: array of correct option ids
    correct_multi: initial.correct_answer?.answers ?? [],
    // Type 3: boolean
    correct_tf: initial.correct_answer?.answer ?? true,
    // Type 4: short text accepted answers
    short_text_answers: initial.question_type === 4 && initial.correct_answer?.answers?.length
      ? initial.correct_answer.answers.map(v => ({ value: v }))
      : [{ value: '' }],
    // Type 6: blank answers array
    blank_answers: initial.question_type === 6 && initial.correct_answer?.answers?.length
      ? initial.correct_answer.answers.map(v => ({ value: v }))
      : [{ value: '' }],
    // Type 7: matching pairs
    pairs: [{ leftId: 1, leftText: '', rightId: 1, rightText: '' }],
    // Type 8: ordering
    ordering_items: [{ id: 1, text: '' }, { id: 2, text: '' }],
    // Type 9: table completion
    table_rows:    ['', ''],
    table_cols:    ["O'zbekiston tarixidagi voqea", "Jahon tarixidagi voqea"],
    table_blanks:  ['a', 'b'],
    table_options: [{ id: 1, text: '' }],
    table_correct: {},
    // Type 10: grouping
    groups:       [{ id: 1, label: 'I guruh' }, { id: 2, label: 'II guruh' }],
    group_options:[{ id: 1, text: '' }],
    group_correct:{},
    // Type 11: multi-blank
    blanks:       [{ id: 1, label: '' }],
    blank_options:[{ id: 'a', text: '' }],
    blank_correct:{},
    // Type 12: multi true/false
    tf_statements:[{ id: 'I', text: '' }],
    tf_verdicts:  {},
  }
}

// ── Build Django API payload from form values ────────────────────────────────
function buildPayload(data) {
  const type = Number(data.question_type)

  let options        = []
  let correct_answer = {}

  switch (type) {
    case 1:
      options        = data.options.map((o) => ({ id: o.id, text: o.text }))
      correct_answer = { answer: Number(data.correct_single) }
      break

    case 2:
      options        = data.options.map((o) => ({ id: o.id, text: o.text }))
      correct_answer = { answers: (data.correct_multi ?? []).map(Number) }
      break

    case 3:
      options        = []
      correct_answer = { answer: data.correct_tf === 'true' || data.correct_tf === true }
      break

    case 4:
      options        = []
      correct_answer = { answers: (data.short_text_answers ?? []).map(b => b.value).filter(Boolean) }
      break

    case 5:
      options        = []
      correct_answer = {}   // Essay — AI graded, no variants
      break

    case 6:
      options        = []
      correct_answer = { answers: (data.blank_answers ?? []).map((b) => b.value) }
      break

    case 7:
      options = data.pairs.map((p) => ({
        left:  { id: p.leftId,  text: p.leftText  },
        right: { id: p.rightId, text: p.rightText },
      }))
      correct_answer = {
        pairs: data.pairs.map((p) => ({ left: p.leftId, right: p.rightId })),
      }
      break

    case 8: // Ordering
      options = data.ordering_items.map(o => ({ id: o.id, text: o.text }))
      correct_answer = { order: data.ordering_items.map(o => o.id) }
      break

    case 9: // Table Completion
      options = data.table_options.map(o => ({ id: o.id, text: o.text }))
      correct_answer = {
        cells: data.table_correct || {},
        table_config: {
          rows:   data.table_rows,
          cols:   data.table_cols,
          blanks: data.table_blanks,
        },
      }
      break

    case 10: // Grouping
      options = data.group_options.map(o => ({ id: o.id, text: o.text }))
      correct_answer = {
        assignments: data.group_correct || {},
        groups:      data.groups,
      }
      break

    case 11: // Multi-blank
      options = data.blank_options.map(o => ({ id: o.id, text: o.text }))
      correct_answer = {
        pairs:  data.blank_correct || {},
        blanks: data.blanks,
      }
      break

    case 12: // Multi True/False
      options = data.tf_statements.map(s => ({ id: s.id, text: s.text }))
      correct_answer = { verdicts: data.tf_verdicts || {} }
      break
  }

  return {
    question_bank:  data.question_bank ? Number(data.question_bank) : null,
    question_type:  type,
    content:        data.content,           // plain text string
    topic:          data.topic || null,
    options,
    correct_answer,
    score:          Number(data.score) || 1,
  }
}

// ── Sub-forms by type ────────────────────────────────────────────────────────

/** Rich-text content input */
function ContentInput({ control, errors }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {t('questions.content')} <span className="text-red-500">*</span>
      </label>
      <Controller
        name="content"
        control={control}
        rules={{ required: true, validate: v => v?.replace(/<[^>]*>/g,'').trim().length > 0 }}
        render={({ field }) => (
          <RichTextEditor
            value={field.value}
            onChange={field.onChange}
            placeholder={t('questions.content')}
          />
        )}
      />
      {errors?.content && (
        <p className="text-xs text-red-500">{t('common.required')}</p>
      )}
    </div>
  )
}

/** Option row with plain text */
function OptionRow({ idx, remove, register, disableRemove }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2 p-2.5 bg-gray-50 dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-600">
      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
        {idx + 1}
      </div>
      <input
        {...register(`options.${idx}.text`)}
        placeholder={`${t('questions.optionText')} ${idx + 1}`}
        className={`${FIELD_CLS}`}
      />
      {!disableRemove && (
        <button type="button" onClick={() => remove(idx)}
          className="text-gray-400 hover:text-red-500 text-lg leading-none">×</button>
      )}
    </div>
  )
}

/** Type 1 — Single Choice */
function SingleChoiceForm({ control, register, watch, fields, append, remove }) {
  const { t } = useTranslation()
  const watchedOptions = watch('options') || []
  function nextId() {
    const ids = watchedOptions.map(o => Number(o.id) || 0)
    return ids.length ? Math.max(...ids) + 1 : 1
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('questions.options')}</label>
        <Button type="button" variant="ghost" size="sm"
          onClick={() => append({ id: nextId(), text: '' })}>
          + {t('questions.addOption')}
        </Button>
      </div>
      {fields.map((field, idx) => {
        const optId = watchedOptions[idx]?.id ?? (idx + 1)
        return (
        <div key={field.id} className="flex items-center gap-3">
          <Controller control={control} name="correct_single"
            render={({ field: f }) => (
              <input type="radio" value={optId}
                checked={Number(f.value) === optId}
                onChange={() => f.onChange(optId)}
                className="accent-blue-600 w-4 h-4 flex-shrink-0"
              />
            )}
          />
          <div className="flex-1">
            <OptionRow idx={idx} remove={remove} register={register} disableRemove={fields.length <= 2} />
          </div>
        </div>
        )
      })}
      <p className="text-xs text-gray-500">{t('questions.correctAnswerSchemas.1')}</p>
    </div>
  )
}

/** Type 2 — Multiple Choice */
function MultipleChoiceForm({ control, register, watch, fields, append, remove }) {
  const { t } = useTranslation()
  const watchedOptions = watch('options') || []
  function nextId() {
    const ids = watchedOptions.map(o => Number(o.id) || 0)
    return ids.length ? Math.max(...ids) + 1 : 1
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('questions.options')}</label>
        <Button type="button" variant="ghost" size="sm"
          onClick={() => append({ id: nextId(), text: '' })}>
          + {t('questions.addOption')}
        </Button>
      </div>
      {fields.map((field, idx) => {
        const optId = watchedOptions[idx]?.id ?? (idx + 1)
        return (
        <div key={field.id} className="flex items-center gap-3">
          <Controller control={control} name="correct_multi"
            render={({ field: f }) => {
              const vals = f.value ?? []
              return (
                <input type="checkbox"
                  checked={vals.includes(optId)}
                  onChange={(e) => {
                    if (e.target.checked) f.onChange([...vals, optId])
                    else f.onChange(vals.filter(v => v !== optId))
                  }}
                  className="accent-blue-600 w-4 h-4 flex-shrink-0"
                />
              )
            }}
          />
          <div className="flex-1">
            <OptionRow idx={idx} remove={remove} register={register} disableRemove={fields.length <= 2} />
          </div>
        </div>
        )
      })}
      <p className="text-xs text-gray-500">{t('questions.correctAnswerSchemas.2')}</p>
    </div>
  )
}

/** Type 3 — True / False */
function TrueFalseForm({ register }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">{t('questions.correctAnswer')}</label>
      <div className="flex gap-4">
        {[
          { value: 'true',  label: '✅ ' + t('common.yes') },
          { value: 'false', label: '❌ ' + t('common.no')  },
        ].map(({ value, label }) => (
          <label key={value} className="flex items-center gap-2.5 cursor-pointer group">
            <input
              type="radio"
              value={value}
              {...register('correct_tf')}
              className="w-4 h-4 accent-blue-600"
            />
            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
              {label}
            </span>
          </label>
        ))}
      </div>
    </div>
  )
}

/** Type 4 — Short Text: accepted answer variants */
function ShortTextAnswersForm({ register, control }) {
  const { fields, append, remove } = useFieldArray({ control, name: 'short_text_answers' })
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 dark:text-slate-300">
          To'g'ri javob variantlari
          <span className="ml-1.5 text-xs text-gray-400 font-normal">(AI barchasi bilan taqqoslaydi)</span>
        </label>
        <button
          type="button"
          onClick={() => append({ value: '' })}
          className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
        >
          <Plus size={13} /> Variant qo'shish
        </button>
      </div>
      {fields.map((field, idx) => (
        <div key={field.id} className="flex items-center gap-2">
          <span className="w-6 text-center text-xs font-semibold text-gray-400">{idx + 1}.</span>
          <input
            {...register(`short_text_answers.${idx}.value`)}
            placeholder={idx === 0 ? "Asosiy to'g'ri javob" : `Variant ${idx + 1}`}
            className={FIELD_CLS + ' flex-1'}
          />
          {fields.length > 1 && (
            <button type="button" onClick={() => remove(idx)} className="text-red-400 hover:text-red-600 p-1">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}
      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
        <span className="text-base leading-none">🤖</span>
        <span>AI talabaning javobini barcha variantlar bilan semantik jihatdan taqqoslaydi. "Oq" va "oppoq" bir xil deb baholanadi.</span>
      </div>
    </div>
  )
}

/** Type 5 — Essay (AI graded, no variants) */
function AiGradedForm({ type }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
      <span className="text-2xl flex-shrink-0">🤖</span>
      <div>
        <p className="text-sm font-semibold text-amber-800">{t(`questions.types.${type}`)}</p>
        <p className="text-xs text-amber-700 mt-0.5">{t(`questions.correctAnswerSchemas.${type}`)}</p>
      </div>
    </div>
  )
}

/** Type 6 — Fill in the Blank */
function FillBlankForm({ register, control }) {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({ control, name: 'blank_answers' })

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
        💡 {t('questions.blankHint')}
      </p>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">{t('questions.correctAnswer')}</label>
        <Button type="button" variant="ghost" size="sm" icon="+" onClick={() => append({ value: '' })}>
          {t('questions.addOption')}
        </Button>
      </div>
      {fields.map((field, idx) => (
        <div key={field.id} className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
            {idx + 1}
          </span>
          <input
            {...register(`blank_answers.${idx}.value`)}
            placeholder={`${t('questions.correctAnswer')} #${idx + 1}`}
            className={`flex-1 ${FIELD_CLS}`}
          />
          {fields.length > 1 && (
            <button type="button" onClick={() => remove(idx)}
              className="text-gray-400 hover:text-red-500 text-xl leading-none">×</button>
          )}
        </div>
      ))}
    </div>
  )
}

/** Type 7 — Matching */
function MatchingForm({ register, control }) {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({ control, name: 'pairs' })

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 text-xs font-semibold text-gray-500 px-1">
        <span>← {t('questions.matchLeft')}</span>
        <span>→ {t('questions.matchRight')}</span>
      </div>
      {fields.map((field, idx) => (
        <div key={field.id} className="grid grid-cols-2 gap-2 items-center bg-gray-50 dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-600 p-3">
          <input
            {...register(`pairs.${idx}.leftText`)}
            placeholder={`${t('questions.matchLeft')} ${idx + 1}`}
            className={FIELD_CLS}
          />
          <div className="flex items-center gap-2">
            <input
              {...register(`pairs.${idx}.rightText`)}
              placeholder={`${t('questions.matchRight')} ${idx + 1}`}
              className={`flex-1 ${FIELD_CLS}`}
            />
            {fields.length > 1 && (
              <button type="button" onClick={() => remove(idx)}
                className="text-gray-400 hover:text-red-500 text-lg leading-none flex-shrink-0">×</button>
            )}
          </div>
        </div>
      ))}
      <Button type="button" variant="secondary" size="sm"
        onClick={() => append({ leftId: fields.length + 1, leftText: '', rightId: fields.length + 1, rightText: '' })}>
        + {t('questions.addPair')}
      </Button>
    </div>
  )
}

/** Type 8 — Ordering */
function OrderingForm({ register, control }) {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({ control, name: 'ordering_items' })

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
        💡 {t('questions.correctAnswerSchemas.8')}
      </p>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('questions.orderingItems', 'Tartiblanadigan elementlar')}</label>
        <Button type="button" variant="ghost" size="sm"
          onClick={() => append({ id: fields.length + 1, text: '' })}>
          + {t('questions.addItem', 'Element qo\'shish')}
        </Button>
      </div>
      {fields.map((field, idx) => (
        <div key={field.id} className="flex items-center gap-2 p-2.5 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-700">
          <span className="w-7 h-7 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
            {idx + 1}
          </span>
          <input
            {...register(`ordering_items.${idx}.text`)}
            placeholder={`Element #${idx + 1}`}
            className={`flex-1 ${FIELD_CLS}`}
          />
          {fields.length > 2 && (
            <button type="button" onClick={() => remove(idx)}
              className="text-gray-400 hover:text-red-500 text-xl leading-none">×</button>
          )}
        </div>
      ))}
      <p className="text-xs text-gray-500">To'g'ri tartib: yuqoridan pastga ketma-ketlik</p>
    </div>
  )
}

/** Type 9 — Table Completion */
function TableForm({ register, watch, setValue }) {
  const { t } = useTranslation()
  const tableRows   = watch('table_rows')   || []
  const tableCols   = watch('table_cols')   || []
  const tableBlanks = watch('table_blanks') || []
  const tableOptions= watch('table_options')|| []
  const tableCorrect= watch('table_correct')|| {}

  function addRow()    { setValue('table_rows',   [...tableRows,   '']) }
  function addCol()    { setValue('table_cols',   [...tableCols,   '']) }
  function addBlank()  { setValue('table_blanks', [...tableBlanks, String.fromCharCode(97 + tableBlanks.length)]) }
  function addOption() { setValue('table_options',[...tableOptions, { id: tableOptions.length + 1, text: '' }]) }

  function setBlankAnswer(blankId, optionId) {
    setValue('table_correct', { ...tableCorrect, [blankId]: Number(optionId) })
  }

  return (
    <div className="space-y-4">
      {/* Rows */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">{t('questions.tableRows', 'Jadval satrlari')}</label>
          <Button type="button" variant="ghost" size="sm" onClick={addRow}>+ {t('questions.addRow', 'Satr qo\'shish')}</Button>
        </div>
        {tableRows.map((_, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-5">{i+1}</span>
            <input
              {...register(`table_rows.${i}`)}
              placeholder={`Satr ${i+1}`}
              className={`flex-1 ${FIELD_CLS}`}
            />
          </div>
        ))}
      </div>
      {/* Cols */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">{t('questions.tableCols', 'Jadval ustunlari')}</label>
          <Button type="button" variant="ghost" size="sm" onClick={addCol}>+ {t('questions.addCol', 'Ustun qo\'shish')}</Button>
        </div>
        {tableCols.map((_, i) => (
          <input key={i} {...register(`table_cols.${i}`)} placeholder={`Ustun ${i+1}`} className={FIELD_CLS} />
        ))}
      </div>
      {/* Blanks */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">{t('questions.tableBlanks', 'Bo\'sh kataklar (harflar)')}</label>
          <Button type="button" variant="ghost" size="sm" onClick={addBlank}>+ {t('questions.addBlank', 'Katak qo\'shish')}</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {tableBlanks.map((b, i) => (
            <span key={i} className="px-3 py-1 bg-gray-100 dark:bg-slate-700 rounded-full text-sm font-mono font-bold">{b}</span>
          ))}
        </div>
      </div>
      {/* Options */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">{t('questions.tableOptions', 'Variantlar')}</label>
          <Button type="button" variant="ghost" size="sm" onClick={addOption}>+ {t('questions.addOption')}</Button>
        </div>
        {tableOptions.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold">{opt.id}</span>
            <input {...register(`table_options.${i}.text`)} placeholder={`Variant ${opt.id}`} className={`flex-1 ${FIELD_CLS}`} />
          </div>
        ))}
      </div>
      {/* Correct mapping */}
      {tableBlanks.length > 0 && tableOptions.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">To'g'ri javoblar (katak → variant)</label>
          {tableBlanks.map((blankId) => (
            <div key={blankId} className="flex items-center gap-3">
              <span className="w-8 text-sm font-bold text-center font-mono bg-gray-100 dark:bg-slate-700 rounded py-1">{blankId}</span>
              <select
                value={tableCorrect[blankId] || ''}
                onChange={e => setBlankAnswer(blankId, e.target.value)}
                className={`flex-1 ${FIELD_CLS}`}
              >
                <option value="">— tanlang —</option>
                {tableOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.id}: {opt.text}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Type 10 — Grouping */
function GroupingForm({ register, watch, setValue }) {
  const { t } = useTranslation()
  const groups      = watch('groups')       || []
  const groupOptions= watch('group_options')|| []
  const groupCorrect= watch('group_correct')|| {}

  function addGroup()  { setValue('groups',        [...groups, { id: groups.length + 1, label: `${groups.length + 1}-guruh` }]) }
  function addOption() { setValue('group_options',  [...groupOptions, { id: groupOptions.length + 1, text: '' }]) }
  function assign(optId, groupId) { setValue('group_correct', { ...groupCorrect, [optId]: Number(groupId) }) }

  return (
    <div className="space-y-4">
      {/* Groups */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">{t('questions.groups', 'Guruhlar')}</label>
          <Button type="button" variant="ghost" size="sm" onClick={addGroup}>+ {t('questions.addGroup', 'Guruh qo\'shish')}</Button>
        </div>
        {groups.map((g, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-pink-100 text-pink-700 flex items-center justify-center text-xs font-bold">{g.id}</span>
            <input {...register(`groups.${i}.label`)} placeholder={`Guruh ${g.id}`} className={`flex-1 ${FIELD_CLS}`} />
          </div>
        ))}
      </div>
      {/* Options */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">{t('questions.options', 'Variantlar')}</label>
          <Button type="button" variant="ghost" size="sm" onClick={addOption}>+ {t('questions.addOption')}</Button>
        </div>
        {groupOptions.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-bold">{opt.id}</span>
            <input {...register(`group_options.${i}.text`)} placeholder={`Variant ${opt.id}`} className={`flex-1 ${FIELD_CLS}`} />
            <select
              value={groupCorrect[opt.id] || ''}
              onChange={e => assign(opt.id, e.target.value)}
              className={`w-36 ${FIELD_CLS}`}
            >
              <option value="">— guruh —</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>{g.label || `Guruh ${g.id}`}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Type 11 — Multi-blank */
function MultiBlankForm({ register, watch, setValue }) {
  const { t } = useTranslation()
  const blanks      = watch('blanks')       || []
  const blankOptions= watch('blank_options')|| []
  const blankCorrect= watch('blank_correct')|| {}

  function addBlank()  { setValue('blanks',        [...blanks, { id: blanks.length + 1, label: '' }]) }
  function addOption() { setValue('blank_options',  [...blankOptions, { id: String.fromCharCode(97 + blankOptions.length), text: '' }]) }
  function assign(blankId, optionId) { setValue('blank_correct', { ...blankCorrect, [blankId]: optionId }) }

  return (
    <div className="space-y-4">
      {/* Options (answer pool) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">{t('questions.blankOptions', 'Variantlar (javob havzasi)')}</label>
          <Button type="button" variant="ghost" size="sm" onClick={addOption}>+ {t('questions.addOption')}</Button>
        </div>
        {blankOptions.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-8 text-center font-bold font-mono text-teal-700 bg-teal-50 rounded py-1">{opt.id}</span>
            <input {...register(`blank_options.${i}.text`)} placeholder={`Variant ${opt.id}`} className={`flex-1 ${FIELD_CLS}`} />
          </div>
        ))}
      </div>
      {/* Blanks with correct pairing */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">{t('questions.blanks', 'Bo\'sh joylar')}</label>
          <Button type="button" variant="ghost" size="sm" onClick={addBlank}>+ {t('questions.addBlankItem', 'Bo\'sh joy qo\'shish')}</Button>
        </div>
        {blanks.map((blank, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-bold">{blank.id}</span>
            <input {...register(`blanks.${i}.label`)} placeholder={`Bo'sh joy ${blank.id} tavsifi`} className={`flex-1 ${FIELD_CLS}`} />
            <select
              value={blankCorrect[blank.id] || ''}
              onChange={e => assign(blank.id, e.target.value)}
              className={`w-28 ${FIELD_CLS}`}
            >
              <option value="">— variant —</option>
              {blankOptions.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.id}: {opt.text.slice(0, 20)}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Type 12 — Multi True/False */
function MultiTrueFalseForm({ register, watch, setValue }) {
  const { t } = useTranslation()
  const statements = watch('tf_statements') || []
  const tfVerdicts = watch('tf_verdicts')   || {}

  const ROMAN = ['I','II','III','IV','V','VI','VII','VIII','IX','X']

  function addStatement() {
    const nextId = ROMAN[statements.length] || String(statements.length + 1)
    setValue('tf_statements', [...statements, { id: nextId, text: '' }])
  }
  function setVerdict(stmtId, verdict) {
    setValue('tf_verdicts', { ...tfVerdicts, [stmtId]: verdict })
  }
  function removeStatement(idx) {
    setValue('tf_statements', statements.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">{t('questions.statements', 'Bandlar')}</label>
        <Button type="button" variant="ghost" size="sm" onClick={addStatement}>
          + {t('questions.addStatement', 'Band qo\'shish')}
        </Button>
      </div>
      {statements.map((stmt, i) => (
        <div key={i} className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-700 space-y-2">
          <div className="flex items-center gap-2">
            <span className="w-8 text-center font-bold text-yellow-700 bg-yellow-100 rounded py-0.5 font-mono text-xs">{stmt.id}</span>
            <input
              {...register(`tf_statements.${i}.text`)}
              placeholder={`Band ${stmt.id}`}
              className={`flex-1 ${FIELD_CLS}`}
            />
            {statements.length > 1 && (
              <button type="button" onClick={() => removeStatement(i)}
                className="text-gray-400 hover:text-red-500 text-xl leading-none">×</button>
            )}
          </div>
          <div className="flex gap-3 ml-10">
            {[
              { val: true,  label: '✅ ' + t('questions.trueLabel', 'To\'g\'ri') },
              { val: false, label: '❌ ' + t('questions.falseLabel', 'Noto\'g\'ri') },
            ].map(({ val, label }) => (
              <button
                key={String(val)} type="button"
                onClick={() => setVerdict(stmt.id, val)}
                className={[
                  'text-xs px-3 py-1.5 rounded-full border font-medium transition-all',
                  tfVerdicts[stmt.id] === val
                    ? (val ? 'bg-green-600 text-white border-green-600' : 'bg-red-600 text-white border-red-600')
                    : 'bg-white dark:bg-slate-700 border-gray-300 text-gray-600 hover:border-gray-400',
                ].join(' ')}
              >
                {label}
              </button>
            ))}
            {tfVerdicts[stmt.id] === undefined && (
              <span className="text-xs text-gray-400 italic">Tanlanmagan</span>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CreateQuestion({ onSaved, initial = null }) {
  const { t }         = useTranslation()
  const [saveStatus,  setSaveStatus]  = useState(null)
  const [saveError,   setSaveError]   = useState(null)
  const [topics,      setTopics]      = useState([])
  const [banks,       setBanks]       = useState([])

  useEffect(() => {
    fetchAllPages(listTopics, { unmerged: 1 }).then(setTopics).catch(() => {})
    fetchAllPages(listQuestionBanks).then(data => {
      setBanks(data)
      if (initial?.question_bank) setValue('question_bank', initial.question_bank)
    }).catch(() => {})
  }, [])

  const {
    register, control, watch, handleSubmit, reset, setValue,
    formState: { errors },
  } = useForm({ defaultValues: defaultValues(initial || {}) })

  const questionType = Number(watch('question_type'))

  // Options field array — shared by types 1 & 2
  const optionsArray = useFieldArray({ control, name: 'options' })


  // ── Submit ─────────────────────────────────────────────────────────
  async function onSubmit(data) {
    setSaveStatus('saving')
    setSaveError(null)
    try {
      const p = buildPayload(data)
      if (initial?.id) {
        await updateQuestion(initial.id, p)
      } else {
        await createQuestion(p)
      }
      setSaveStatus('success')
      if (!initial) reset(defaultValues())
      onSaved?.()
    } catch (err) {
      setSaveError(err.response?.data ? JSON.stringify(err.response.data) : err.message)
      setSaveStatus('error')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>

      {/* ── Row 0: Question Bank (required) ──────────────────────── */}
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('nav.questionBanks', 'Savollar bazasi')} <span className="text-red-500">*</span>
        </label>
        <select
          {...register('question_bank', { required: true })}
          className={`${FIELD_CLS}${errors?.question_bank ? ' border-red-400' : ''}`}
        >
          <option value="">— Savollar bazasini tanlang —</option>
          {banks.map(b => (
            <option key={b.id} value={b.id}>{b.name}{b.subject_name ? ` (${b.subject_name})` : ''}</option>
          ))}
        </select>
        {errors?.question_bank && <p className="text-xs text-red-500">{t('common.required', 'Majburiy maydon')}</p>}
      </div>

      {/* ── Row 1: Type ──────────────────────────────────────── */}
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('questions.questionType')} <span className="text-red-500">*</span>
        </label>
        <select
          {...register('question_type', { required: true, valueAsNumber: true })}
          className={FIELD_CLS}
        >
          {QUESTION_TYPES.map((qt) => (
            <option key={qt} value={qt}>
              {qt}. {t(`questions.types.${qt}`)}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
          <Badge variant={TYPE_COLORS[questionType] ?? 'gray'}>{t(`questions.types.${questionType}`)}</Badge>
          {t(`questions.typeDescriptions.${questionType}`)}
        </p>
      </div>

      {/* ── Row 2: Topic selector ────────────────────────────────────── */}
      {topics.length > 0 && (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
            {t('nav.topics', 'Mavzu')}
          </label>
          <select {...register('topic')} className={FIELD_CLS}>
            <option value="">— {t('subjects.noSubject', 'Mavzusiz')} —</option>
            {topics.map(tp => (
              <option key={tp.id} value={tp.id}>{tp.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* ── Row 3: Question content ──────────────────────────────────── */}
      <ContentInput control={control} errors={errors} />

      {/* ── Row 3: Type-specific answer form ─────────────────────────── */}
      <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 p-4 space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 flex items-center gap-2">
          <span className="w-5 h-5 bg-blue-600 text-white rounded flex items-center justify-center text-xs">✓</span>
          {t('questions.correctAnswer')}
        </h3>

        {questionType === 1 && (
          <SingleChoiceForm
            control={control} register={register} watch={watch}
            fields={optionsArray.fields}
            append={optionsArray.append}
            remove={optionsArray.remove}
          />
        )}
        {questionType === 2 && (
          <MultipleChoiceForm
            control={control} register={register} watch={watch}
            fields={optionsArray.fields}
            append={optionsArray.append}
            remove={optionsArray.remove}
          />
        )}
        {questionType === 3 && <TrueFalseForm register={register} />}
        {questionType === 4 && <ShortTextAnswersForm register={register} control={control} />}
        {questionType === 5 && <AiGradedForm type={5} />}
        {questionType === 6 && <FillBlankForm register={register} control={control} />}
        {questionType === 7 && <MatchingForm register={register} control={control} />}
        {questionType === 8 && (
          <OrderingForm
            register={register} control={control}
          />
        )}
        {questionType === 9 && (
          <TableForm register={register} watch={watch} setValue={setValue} />
        )}
        {questionType === 10 && (
          <GroupingForm register={register} watch={watch} setValue={setValue} />
        )}
        {questionType === 11 && (
          <MultiBlankForm register={register} watch={watch} setValue={setValue} />
        )}
        {questionType === 12 && (
          <MultiTrueFalseForm register={register} watch={watch} setValue={setValue} />
        )}
      </div>

      {/* ── Status messages ───────────────────────────────────────────── */}
      {saveStatus === 'success' && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3 rounded-xl">
          <span>✓</span> {t('questions.saved')}
        </div>
      )}
      {saveStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
          <span className="font-semibold">{t('common.error')}:</span> {saveError}
        </div>
      )}

      {/* ── Actions ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
        <Button
          type="button" variant="secondary"
          onClick={() => reset(defaultValues())}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit" variant="primary"
          loading={saveStatus === 'saving'}
          icon={<Save size={15} />}
        >
          {t('common.save')}
        </Button>
      </div>
    </form>
  )
}

